/**
 * Contact Resolution API Client
 * Handles bulk contact operations from CSV uploads
 */

import { API_ROUTES, IntegrationSource } from '@super/shared-types'
import { BaseApiClient } from './base-client'
import type { ContactResolutionPlan } from '@super/shared-types'

/**
 * Contact resolution execution request
 */
export interface ContactResolutionRequest {
  contactResolutionPlan: ContactResolutionPlan
  companyId: string
  source: IntegrationSource
  options?: {
    transactional?: boolean
  }
}

/**
 * Contact resolution execution response
 */
export interface ContactResolutionResponse {
  success: boolean
  planId: string
  results: {
    createdPersons: number
    updatedPersons: number
    createdRelationships: number
    skippedDuplicates: number
    errors: number
  }
  errors?: Array<{
    operation: string
    identifier: string
    error: string
    details?: any
  }>
  executionTimeMs: number
}

/**
 * Contact resolution validation response
 */
export interface ValidateContactResolutionResponse {
  valid: boolean
  errors: Array<{
    operation: string
    identifier: string
    error: string
    field?: string
  }>
  warnings: Array<{
    operation: string
    identifier: string
    message: string
  }>
  duplicates: Array<{
    email: string
    existingPersons: Array<{
      id: string
      name: string
    }>
  }>
}

/**
 * Contact Resolution API Client
 */
export class ContactResolutionApiClient extends BaseApiClient {
  /**
   * Execute a contact resolution plan
   * @param request - The contact resolution request
   * @returns The contact resolution response
   */
  async executeContactResolution(
    request: ContactResolutionRequest,
  ): Promise<ContactResolutionResponse> {
    const response = await this.request<{
      data: ContactResolutionResponse
    }>(API_ROUTES.CONTACT_RESOLUTION.EXECUTE, {
      method: 'POST',
      body: JSON.stringify(request),
    })
    return response.data
  }

  /**
   * Validate a contact resolution plan before execution
   * @param request - The contact resolution request
   * @returns The contact resolution validation response
   */
  async validateContactResolution(
    request: ContactResolutionRequest,
  ): Promise<ValidateContactResolutionResponse> {
    const response = await this.request<{
      data: ValidateContactResolutionResponse
    }>(API_ROUTES.CONTACT_RESOLUTION.VALIDATE, {
      method: 'POST',
      body: JSON.stringify(request),
    })
    return response.data
  }
}

// Export singleton instance
export const contactResolutionApiClient = new ContactResolutionApiClient()
